import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth } from "../auth/authContext.tsx";
import { toolDefinitions } from "./toolRegistry.ts";
import { DraggableWindow } from "../components/ui/draggableWindow.tsx";
import styles from "./ToolsDrawer.module.scss";

interface ToolsContextValue {
  openTools: (toolId?: string) => void;
  closeTools: () => void;
  switchTool: (toolId: string) => void;
  isOpen: boolean;
  activeToolId: string;
}

const ToolsContext = createContext<ToolsContextValue | null>(null);

export function useTools(): ToolsContextValue {
  const ctx = useContext(ToolsContext);
  if (!ctx) throw new Error("useTools must be used within ToolsProvider");
  return ctx;
}

/** The tools drawer when there is one, else null - for components that are also
 *  rendered outside the app layout and only offer a shortcut into a tool. */
export function useOptionalTools(): ToolsContextValue | null {
  return useContext(ToolsContext);
}

export function ToolsProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const DEFAULT_TOOL_ID = "kiosklocation";
  const [activeToolId, setActiveToolId] = useState(
    toolDefinitions.find((t) => t.id === DEFAULT_TOOL_ID)?.id
      ?? toolDefinitions[0]?.id
      ?? "",
  );

  const openTools = useCallback((toolId?: string) => {
    if (toolId) setActiveToolId(toolId);
    setIsOpen(true);
  }, []);

  const closeTools = useCallback(() => setIsOpen(false), []);

  const switchTool = useCallback((toolId: string) => {
    setActiveToolId(toolId);
    setIsOpen(true);
  }, []);

  // Keyboard shortcuts: Ctrl+Shift+<key>. Reject Ctrl+Shift+Alt so SA's
  // triple-modifier shortcuts don't double-fire here.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.ctrlKey || !e.shiftKey || e.altKey) return;
      // Match on e.code (KeyL, Digit4, Numpad4) — stable across layouts
      // and across NumLock state for the digit row vs numpad.
      const code = e.code;
      let key = "";
      if (code.startsWith("Key")) key = code.slice(3);
      else if (code.startsWith("Digit")) key = code.slice(5);
      else if (code.startsWith("Numpad")) key = code.slice(6);
      else return;
      const tool = toolDefinitions.find((t) => t.shortcutKey === key);
      if (tool) {
        e.preventDefault();
        setActiveToolId(tool.id);
        setIsOpen(true);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const value = useMemo(
    () => ({ openTools, closeTools, switchTool, isOpen, activeToolId }),
    [openTools, closeTools, switchTool, isOpen, activeToolId]
  );

  return <ToolsContext.Provider value={value}>{children}</ToolsContext.Provider>;
}

export function ToolsDrawer() {
  const { isOpen, activeToolId, closeTools, switchTool } = useTools();
  const { canRead, cache } = useAuth();

  const pages = useMemo(() => cache?.PageInfo?.Page ?? [], [cache]);

  const availableTools = useMemo(
    () =>
      toolDefinitions.filter(
        (t) => !t.checkAccess || t.checkAccess(canRead, pages)
      ),
    [canRead, pages]
  );

  const activeTool = useMemo(
    () => availableTools.find((t) => t.id === activeToolId) ?? availableTools[0],
    [availableTools, activeToolId]
  );

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") closeTools();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, closeTools]);

  if (!isOpen || !activeTool) return null;

  const ActiveComponent = activeTool.component;

  return (
    <DraggableWindow
      open={isOpen}
      onClose={closeTools}
      title="Tools"
      width={936}
      height={700}
    >
      <div className={styles.windowHeader}>
        <select
          className={styles.toolSelect}
          value={activeTool.id}
          onChange={(e) => switchTool(e.target.value)}
        >
          {availableTools.map((t) => (
            <option key={t.id} value={t.id}>
              {t.shortcutKey ? `${t.name}  ·  Ctrl+Shift+${t.shortcutKey}` : t.name}
            </option>
          ))}
        </select>
      </div>
      <div className={styles.windowBody}>
        <Suspense fallback={<div style={{ padding: "2rem", textAlign: "center" }}>Loading...</div>}>
          <ActiveComponent />
        </Suspense>
      </div>
    </DraggableWindow>
  );
}
